// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function NolaAiChatFriendTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Nola AI Chat Friend"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of “Nola
        AI Chat Friend” (the “App”). By downloading, installing, accessing, or
        using the App, you agree to be bound by these Terms. If you do not
        agree, you must uninstall the App and discontinue all use. Where
        required by platform rules or local law, additional terms may apply; in
        case of conflict with App Store or Google Play terms, the platform terms
        may prevail to the extent required.
      </p>

      <div className={s.title}>
        2) Description of the App (AI Companion, Non-Professional Use)
      </div>
      <p>
        The App provides an AI-based chat companion (“Nola” or similar persona)
        that can exchange text messages with you in a messenger-style interface.
        You may use the App to talk about everyday topics, share thoughts,
        brainstorm ideas, or receive light-hearted suggestions and supportive
        messages. The App is intended for general conversation and productivity
        only. It is not a therapist, counselor, or professional advisor and
        does not provide medical, mental health, legal, financial, or other
        professional services.
      </p>
      <p>
        AI-generated responses may be inaccurate, incomplete, biased, or not
        suitable for your specific situation. You are responsible for reviewing
        and verifying all information before acting on it or relying on it. If
        you are facing health, safety, or serious personal issues, you should
        seek help from qualified professionals, trusted people in your life, or
        emergency services in your area rather than relying on the App.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) Accounts, Registration, and Security</div>
      <p>
        The App may require or offer registration using Firebase Authentication
        or similar services (for example, sign-in with email, Google, or Apple
        ID). You are responsible for maintaining the confidentiality of your
        account credentials and for all activity that occurs under your account.
        You must promptly notify us of any suspected unauthorized access or
        security issues. We may suspend or restrict access if we reasonably
        believe your account has been compromised or used in violation of these
        Terms.
      </p>

      <div className={s.title}>
        5) AI Chat Behavior, Boundaries, and Limitations
      </div>
      <p>
        Conversations in the App are generated automatically by AI models based
        on patterns learned from data. The AI is not a real person, has no
        feelings or consciousness, and cannot guarantee factual accuracy. The AI
        may respond in a friendly, conversational tone and may use emojis or
        informal language, but any sense of personality is simulated.
      </p>
      <p>
        You must not treat the App as a replacement for real-life friendships,
        family relationships, or professional support. The AI may attempt to be
        encouraging or supportive, but it is not equipped to handle emergencies
        or serious mental health situations. If you feel overwhelmed, unsafe, or
        in danger, you should contact local emergency services or reach out to
        trusted people in your life instead of relying on the App.
      </p>

      <div className={s.title}>6) Message Limits, Free Tier, and Fair Use</div>
      <p>
        The App may impose usage limits, such as a maximum number of messages
        per day or restrictions on accessible chat history, especially for free
        users. For example, the free version may allow a limited number of
        messages per day and only access to the most recent conversation, while
        paid subscribers may receive higher or unlimited message quotas and full
        history access.
      </p>
      <p>
        We may apply fair-use policies to protect the service from abuse or
        overload, such as rate limiting or temporary restrictions. Limits and
        thresholds may be stored locally, enforced via backend services, or
        managed through third-party infrastructure, and may be adjusted over
        time to maintain stability and quality.
      </p>

      <div className={s.title}>7) Chat History, Storage, and Encryption</div>
      <p>
        The App may store your messages and AI responses locally on your device
        (for example, using SQLite or a similar database) and may optionally
        sync selected history to cloud services such as Firebase Firestore for
        backup or multi-device access. In the free version, history access may
        be limited (for example, only the last chat), while premium plans may
        unlock full history.
      </p>
      <p>
        Local storage may be encrypted, but no system is perfectly secure.
        Deleting the App or clearing local data may permanently remove stored
        conversations from your device. Cloud-backed data, where used, may
        persist until deleted as described in the App or in the Privacy Policy.
        We may delete or aggregate older history to manage storage, improve
        performance, or comply with legal obligations.
      </p>

      <div className={s.title}>8) Avatars, Profiles, and Personalization</div>
      <p>
        The App may allow you to choose or customize an AI profile (for example,
        avatar image, name, short status) and to select from a set of available
        themes or styles. These personalization options are cosmetic and do not
        change the underlying nature of the AI as a non-human system. Some
        avatars or themes may be available only to paid subscribers.
      </p>
      <p>
        You must not upload or use avatar images or profile names that are
        illegal, hateful, infringing, misleading, or otherwise violate these
        Terms or platform rules. We may remove or restrict access to specific
        assets if they are inappropriate or violate rights.
      </p>

      <div className={s.title}>9) Subscriptions, Trials, and Billing</div>
      <p>
        Certain features—such as unlimited messages, full chat history, or
        exclusive avatars—may be available only through paid auto-renewable
        subscriptions or other in-app purchases (“Subscriptions”) such as weekly
        or monthly plans. Typical pricing may include a free trial period
        followed by recurring charges, but exact prices, durations, and offers
        may vary by region and may change over time as displayed on the paywall
        screen and in the app stores.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are determined by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>10) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features. An internet connection may be required to validate or refresh
        entitlements after long offline periods. If platform billing reverses,
        refunds, or flags a transaction as fraudulent, related entitlements may
        be limited, suspended, or revoked.
      </p>

      <div className={s.title}>11) Acceptable Use and Content Rules</div>
      <p>
        You agree to use the App only for lawful and respectful purposes and
        not to:
      </p>
      <ul>
        <li>
          send content that is illegal, discriminatory, exploitative, hateful,
          or harassing;
        </li>
        <li>
          request or generate content that promotes violence, self-harm, or
          other dangerous activities;
        </li>
        <li>
          share personal or sensitive information about other people without
          their consent;
        </li>
        <li>
          use the App to impersonate others, deceive people, or conduct fraud;
        </li>
        <li>
          reverse engineer, decompile, or attempt to extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, message limits, or paywalls
          separating free and paid features;
        </li>
        <li>
          use automated tools or scripts to overload or misuse the App or its
          APIs.
        </li>
      </ul>
      <p>
        If we reasonably believe your use violates these rules or applicable
        law, we may restrict, suspend, or terminate your access and, where
        appropriate, notify relevant service providers or authorities.
      </p>

      <div className={s.title}>12) Third-Party Services and AI Providers</div>
      <p>
        The App may send your messages and related data to third-party AI
        providers (such as OpenAI or similar services) via secure HTTP requests
        to generate responses. These providers process your content under their
        own terms and privacy policies and may use it as allowed under those
        policies and applicable agreements. The App also relies on services such
        as Firebase (for authentication, storage, analytics, and push
        notifications) and RevenueCat (for subscriptions).
      </p>
      <p>
        We do not control the internal operation of these third-party systems
        and are not responsible for their outages, limitations, or policy
        changes. Some features may be updated, limited, or removed based on
        third-party requirements, technical constraints, or legal developments.
      </p>

      <div className={s.title}>13) Intellectual Property</div>
      <p>
        You retain ownership of the original content you submit (for example,
        messages and prompts), subject to rights of third parties. You are
        responsible for ensuring that your use of AI-generated output complies
        with applicable laws, including intellectual property and privacy laws.
      </p>
      <p>
        All software, designs, avatars provided by the App, trademarks, logos,
        and other materials (excluding your content and third-party model
        outputs) are owned by the publisher or its licensors and are protected
        by intellectual property laws. Subject to these Terms and any
        Subscription limitations, you are granted a limited, non-exclusive,
        non-transferable, revocable license to use the App and its features for
        personal use.
      </p>

      <div className={s.title}>14) Analytics, Notifications, and Engagement</div>
      <p>
        We may use analytics tools (for example, Firebase Analytics or other
        providers) to understand how the App is used, measure feature adoption,
        and improve reliability and user experience. Typical events may include
        app launches, screen views, paywall interactions, and purchases.
        Aggregated or de-identified data may be used for product decisions.
        Where required by law, we will obtain necessary permissions or provide
        options to manage analytics preferences.
      </p>
      <p>
        The App may also send push notifications (for example, reminders that
        your AI companion is available to chat). You can control push
        notifications via system settings and, where available, in-app
        preferences.
      </p>

      <div className={s.title}>15) No Warranties</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and non-infringement.
        We do not warrant that AI responses will be accurate, reliable, or
        suitable for any particular purpose, nor that the App will be available
        without interruption, errors, or security issues.
      </p>

      <div className={s.title}>16) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        data (including loss of chat history or settings), or business
        interruption arising from or related to your use of or inability to use
        the App or AI responses. Our total aggregate liability to you for direct
        damages arising out of or relating to the App will be limited to the
        amount you paid for Subscriptions during the twelve (12) months prior to
        the claim, or the minimum amount permitted by applicable law if greater.
        Some jurisdictions do not allow certain limitations or exclusions, so
        parts of this section may not apply to you.
      </p>

      <div className={s.title}>17) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your content, your reliance on AI
        responses, your breach of these Terms, or your violation of any rights
        of a third party.
      </p>

      <div className={s.title}>18) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>19) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Nola AI Chat Friend, which explains how information is collected, used,
        stored, and shared, including chat content, analytics, and technical
        data. In case of conflict between these Terms and the Privacy Policy
        regarding data processing, the Privacy Policy will guide how we handle
        personal data, while these Terms govern your use of the App and its
        features. You should review the Privacy Policy regularly to understand
        current practices.
      </p>

      <div className={s.title}>20) Changes and Contact Information</div>
      <p>
        We may update the App, add or remove features (including free and
        premium capabilities), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or via other reasonable means. By continuing to use the App after
        changes take effect, you accept the updated Terms. If you do not agree
        to the revised Terms, you must stop using the App and uninstall it.
      </p>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account-specific
        or purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
